'use client'

export const dynamic = 'force-dynamic'

import { Suspense, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { toast } from '@warfighter/ui'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import { Input } from '@/components/ui/input'
import { PasswordRequirements } from '@/components/PasswordRequirements'
import { isPasswordValid } from '@/lib/password'
import { checkRateLimit } from '@/lib/rate-limit'

function SignupContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null)
  const [notice, setNotice] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const onboardingParams = searchParams.toString()
  const onboardingUrl = onboardingParams ? `/onboarding?${onboardingParams}` : '/onboarding'

  const handleOAuth = async (provider: 'google' | 'apple') => {
    setOauthLoading(provider)
    const supabase = createBrowserSupabaseClient()
    // OAuth callback lands on /login which runs resolveRedirect
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/login`
      }
    })
    setOauthLoading(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      checkRateLimit('signup')
    } catch (err) {
      toast.warning((err as Error).message)
      return
    }
    setLoading(true)

    const supabase = createBrowserSupabaseClient()
    const { data, error } = await supabase.auth.signUp({ email, password })

    setLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }

    if (!data.session) {
      setNotice('Check your email to confirm your account, then log in.')
      return
    }

    // New signups always go to onboarding (no profile exists yet)
    router.push(onboardingUrl)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-10">
        <h1 className="text-2xl font-bold text-slate-900">Create your account</h1>
        <p className="mt-2 text-slate-500">
          Save your scenarios and unlock the full city breakdown.
        </p>

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={() => handleOAuth('google')}
            disabled={oauthLoading !== null}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {oauthLoading === 'google' ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            Continue with Google
          </button>

          {/* TODO: Apple OAuth — defer to post-launch */}
          {/* <button
            type="button"
            onClick={() => handleOAuth('apple')}
            disabled={oauthLoading !== null}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {oauthLoading === 'apple' ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#111827"
                  d="M16.4 3.8c-.7.9-1.9 1.6-3.1 1.5-.1-1.1.4-2.2 1.1-2.9.7-.8 2-1.5 3.1-1.5.1 1.1-.4 2.1-1.1 2.9zm2.8 7.1c-.1-2.1 1.7-3.1 1.8-3.2-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.6 2.2-1.6 2.7-.4 6.7 1.1 8.9.7 1.1 1.6 2.3 2.8 2.3 1.1 0 1.6-.7 2.9-.7 1.3 0 1.7.7 2.9.7 1.2 0 2-1.1 2.7-2.2.9-1.3 1.2-2.6 1.2-2.7-.1 0-2.2-.8-2.2-3.6z"
                />
              </svg>
            )}
            Continue with Apple
          </button> */}
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Or continue with email
          </span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {notice && <p className="mb-4 text-sm text-emerald-600">{notice}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-600">Email address</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {password && <PasswordRequirements password={password} />}
          </div>
          <button
            type="submit"
            disabled={loading || !isPasswordValid(password)}
            className="flex h-12 w-full items-center justify-center rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/20 transition hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign Up'}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => router.push(searchParams.toString() ? `/login?${searchParams}` : '/login')}
            className="font-semibold text-blue-600 hover:underline"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  )
}

export default function SignupPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-10">
            <div className="h-4 w-40 rounded bg-slate-200" />
            <div className="mt-3 h-3 w-56 rounded bg-slate-100" />
          </div>
        </div>
      }
    >
      <SignupContent />
    </Suspense>
  )
}
